import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Award, BookOpen, GraduationCap, Flame, X, ArrowRight, Crown, Sparkles, Receipt, Trophy } from "lucide-react";
import { MEDAL_IMAGES } from "../lib/constants";
import { MEDALS, getMedal } from "../lib/medals";
import { checkAndResetLostStreak } from "../lib/storage";
import { ProfileBar } from "../components/ProfileBar";
import { PageShell } from "../components/PageShell";
import { useEntitlement } from "../contexts/EntitlementContext";

function BillingBanner({ entitlement, onUpgrade, onManage }) {
  // Premium active (paid)
  if (entitlement.premium) {
    return (
      <button
        onClick={onManage}
        className="w-full text-left mb-6 bg-gradient-to-br from-[#FFF6CC] to-white border-2 border-[#FFD700] rounded-2xl p-4 flex items-center gap-3 hover:shadow-md transition-shadow"
        data-testid="dashboard-premium-banner"
      >
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFD700] to-[#FF9500] flex items-center justify-center shrink-0">
          <Crown className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-lg font-bold text-[#7A5C00]">¡Premium activo!</p>
          <p className="text-sm text-[#5C5C60]">
            Practica sin límites · Toca para ver tu suscripción
          </p>
        </div>
        <Receipt className="w-5 h-5 text-[#7A5C00] shrink-0" strokeWidth={2.5} />
      </button>
    );
  }
  // Trial active
  if (entitlement.trial_active) {
    return (
      <button
        onClick={onUpgrade}
        className="w-full text-left mb-6 bg-gradient-to-br from-[#E8F9EE] to-white border-2 border-[#34C759] rounded-2xl p-3 sm:p-4 flex items-center gap-3 hover:shadow-md transition-shadow"
        data-testid="dashboard-trial-banner"
      >
        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-[#34C759] flex items-center justify-center shrink-0">
          <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base sm:text-lg font-bold text-[#1A6B2F] leading-tight">
            Prueba gratis · {entitlement.trial_days_left} día{entitlement.trial_days_left === 1 ? "" : "s"} restantes
          </p>
          <p className="text-xs sm:text-sm text-[#5C5C60] mt-0.5">
            Toca para hacerte Premium
          </p>
        </div>
        <div
          className="inline-flex items-center gap-1.5 bg-[#34C759] hover:bg-[#2BAA4A] text-white font-bold text-xs sm:text-sm rounded-full px-3 sm:px-4 py-1.5 sm:py-2 transition-colors shrink-0"
          data-testid="dashboard-trial-upgrade-btn"
        >
          <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={3} />
          <span>Premium</span>
        </div>
      </button>
    );
  }
  // Free user (post-trial or no trial yet — guest)
  const isGuest = entitlement.guest;
  const remaining = Math.max(0, (entitlement.free_daily_limit || 1) - (entitlement.rounds_today || 0));
  return (
    <div
      className="mb-6 bg-white border-2 border-gray-200 rounded-2xl p-4 flex items-center gap-3"
      data-testid="dashboard-free-banner"
    >
      <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center shrink-0">
        <Crown className="w-7 h-7 text-gray-400" strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-heading text-base sm:text-lg font-bold text-[#1C1C1E]">
          {isGuest
            ? "Modo invitado · Plan gratis"
            : "Plan gratis · 1 ronda al día"}
        </p>
        <p className="text-sm text-[#5C5C60]">
          {remaining > 0
            ? `Te queda ${remaining} ronda hoy. Hazte Premium para practicar sin límites.`
            : "Ya completaste tu ronda gratis de hoy. Vuelve mañana o hazte Premium."}
        </p>
      </div>
      <button
        onClick={onUpgrade}
        className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#FFD700] to-[#FF9500] hover:opacity-95 text-white font-bold text-sm rounded-full px-3 sm:px-4 py-2 transition-colors shrink-0"
        data-testid="dashboard-free-upgrade-btn"
      >
        <Crown className="w-4 h-4" strokeWidth={3} />
        <span className="hidden sm:inline">Hazte Premium</span>
        <span className="sm:hidden">Premium</span>
      </button>
    </div>
  );
}

// Categories — for now Primaria + Secundaria; more can be added here.
const CATEGORIES = [
  {
    id: "primaria",
    name: "Primaria",
    description: "Suma, resta, multi, división, porcentajes, fracciones y decimales",
    color: "#FF9500",
    colorDark: "#B05A00",
    icon: BookOpen,
    target: "/primaria",
    available: true,
  },
  {
    id: "secundaria",
    name: "Secundaria",
    description: "Álgebra básica · más temas en camino",
    color: "#7B61FF",
    colorDark: "#3D2EA0",
    icon: GraduationCap,
    target: "/secundaria",
    available: true,
  },
];

export default function Dashboard({ profile, onReset }) {
  const navigate = useNavigate();
  const unlocked = profile.medals || [];
  const totalMedals = MEDALS.length;
  const [lostStreak, setLostStreak] = useState(null);
  const { entitlement } = useEntitlement();

  useEffect(() => {
    const result = checkAndResetLostStreak(profile);
    if (result.lost) {
      setLostStreak({ previousCount: result.previousCount });
      if (onReset) onReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageShell>
      <ProfileBar profile={profile} onReset={onReset} />

      <AnimatePresence>
        {lostStreak && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="mt-4 sm:mt-6 bg-white rounded-3xl border-4 border-b-[6px] border-[#FF6B6B] p-4 sm:p-5 flex items-start gap-3 sm:gap-4"
            data-testid="lost-streak-banner"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#FFEAEA] flex items-center justify-center shrink-0">
              <Flame className="w-7 h-7 sm:w-8 sm:h-8 text-[#FF6B6B]" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg sm:text-xl font-bold text-[#803535]">
                ¡Perdiste tu racha! 😢
              </h3>
              <p className="text-sm text-[#5C5C60] mt-0.5">
                Tu racha de <strong>{lostStreak.previousCount} día{lostStreak.previousCount === 1 ? "" : "s"}</strong> se reinició.
                ¡Completa una ronda hoy para empezar de nuevo!
              </p>
            </div>
            <button
              onClick={() => setLostStreak(null)}
              className="shrink-0 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              aria-label="Cerrar aviso"
              data-testid="lost-streak-dismiss"
            >
              <X className="w-4 h-4 text-[#5C5C60]" strokeWidth={3} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-8 sm:mt-12"
      >
        {/* Premium status banner */}
        {entitlement && (
          <BillingBanner
            entitlement={entitlement}
            onUpgrade={() => navigate("/upgrade")}
            onManage={() => navigate("/mi-suscripcion")}
          />
        )}

        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-2">
          Elige una categoría
        </h2>
        <p className="text-base sm:text-lg text-[#5C5C60] mb-6 sm:mb-8 max-w-xl">
          ¡Hola {profile.name}! Empieza por elegir una categoría para practicar.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-testid="categories-grid">
          {CATEGORIES.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <motion.button
                key={cat.id}
                onClick={() => cat.available && navigate(cat.target)}
                disabled={!cat.available}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, type: "spring", bounce: 0.4 }}
                whileHover={cat.available ? { y: -4 } : {}}
                whileTap={cat.available ? { scale: 0.97 } : {}}
                className={`clay-btn relative rounded-[2rem] p-5 sm:p-6 text-left text-white border-b-[8px] active:border-b-0 overflow-hidden ${
                  cat.available ? "" : "opacity-50 cursor-not-allowed"
                }`}
                style={{ backgroundColor: cat.color, borderBottomColor: cat.colorDark }}
                data-testid={`category-card-${cat.id}`}
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center">
                    <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={2.5} />
                  </div>
                  {cat.available ? (
                    <ArrowRight className="w-6 h-6 text-white/80" strokeWidth={3} />
                  ) : (
                    <span className="text-xs font-bold uppercase tracking-wider bg-white/25 px-2 py-1 rounded-full">
                      Próximamente
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-1">{cat.name}</h3>
                <p className="text-sm sm:text-base text-white/85">{cat.description}</p>
              </motion.button>
            );
          })}

          {/* Medals card */}
          <motion.button
            onClick={() => navigate("/medallas")}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: CATEGORIES.length * 0.08, type: "spring", bounce: 0.4 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.97 }}
            className="clay-btn relative rounded-[2rem] p-5 sm:p-6 text-left bg-white border-4 border-b-[8px] border-[#FFD700] active:border-b-4 overflow-hidden"
            data-testid="medals-card"
          >
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#FFF6CC] flex items-center justify-center">
                <Award className="w-8 h-8 sm:w-10 sm:h-10 text-[#FFD700]" strokeWidth={2.5} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider bg-[#FFF6CC] text-[#7A5C00] px-2 py-1 rounded-full">
                {unlocked.length}/{totalMedals}
              </span>
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1C1E] mb-1">
              Mis Medallas
            </h3>
            <p className="text-sm sm:text-base text-[#5C5C60]">Ver tu colección</p>

            {unlocked.length > 0 && (
              <div className="flex -space-x-2 mt-3">
                {unlocked.slice(0, 4).map((id) => {
                  const m = getMedal(id);
                  if (!m) return null;
                  return (
                    <img
                      key={id}
                      src={MEDAL_IMAGES[m.tier]}
                      alt={m.name}
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-white object-contain bg-white"
                    />
                  );
                })}
              </div>
            )}
          </motion.button>

          {/* Ranking card */}
          <motion.button
            onClick={() => navigate("/ranking")}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (CATEGORIES.length + 1) * 0.08, type: "spring", bounce: 0.4 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.97 }}
            className="clay-btn relative rounded-[2rem] p-5 sm:p-6 text-left bg-white border-4 border-b-[8px] border-[#FF9500] active:border-b-4 overflow-hidden"
            data-testid="ranking-card"
          >
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#FFEEDC] flex items-center justify-center">
                <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-[#FF9500] fill-[#FF9500]" strokeWidth={2} />
              </div>
              <span className="text-xs font-bold uppercase tracking-wider bg-[#FFEEDC] text-[#B05A00] px-2 py-1 rounded-full">
                🏆 Nuevo
              </span>
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1C1E] mb-1">
              Ranking
            </h3>
            <p className="text-sm sm:text-base text-[#5C5C60]">Compite por los primeros lugares</p>
          </motion.button>
        </div>

        {/* Stats summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 sm:mt-10 grid grid-cols-3 gap-3 sm:gap-4"
        >
          <div className="bg-white rounded-2xl border-4 border-white shadow-sm p-4 text-center" data-testid="stat-rounds">
            <p className="text-xs font-bold uppercase tracking-wider text-[#5C5C60]">Rondas</p>
            <p className="font-heading text-2xl sm:text-3xl font-bold text-[#1C1C1E]">
              {profile.stats.rounds || 0}
            </p>
          </div>
          <div className="bg-[#E8F9EE] rounded-2xl border-4 border-white shadow-sm p-4 text-center" data-testid="stat-correct">
            <p className="text-xs font-bold uppercase tracking-wider text-[#1A6B2F]">Aciertos</p>
            <p className="font-heading text-2xl sm:text-3xl font-bold text-[#1A6B2F]">
              {profile.stats.correct || 0}
            </p>
          </div>
          <div className="bg-[#FFF6CC] rounded-2xl border-4 border-white shadow-sm p-4 text-center" data-testid="stat-points">
            <p className="text-xs font-bold uppercase tracking-wider text-[#7A5C00]">Puntos</p>
            <p className="font-heading text-2xl sm:text-3xl font-bold text-[#7A5C00]">
              {profile.points || 0}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </PageShell>
  );
}
